import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AnimatePresence, motion } from 'framer-motion';
import { Lock, CreditCard, ExternalLink, LogOut, MailQuestion , Sparkles } from 'lucide-react';
import { useAuth } from './AuthContext';
import { getApiUrl } from '../utils/api';

const API = getApiUrl();

/**
 * V5.99.9 — SubscriptionGateContext.
 *
 * Installs an axios response interceptor that catches HTTP 402 from the API,
 * extracts the structured `detail` payload set by `_enforce_subscription_gate`
 * on the backend and displays a full-screen blocking modal. The user can:
 *   • Open the Stripe billing portal (subscription/portal endpoint)
 *   • Email support
 *   • Log out
 *
 * The modal is rendered globally so every page is blocked the moment the
 * trial expires or the payment fails.
 */

const SubscriptionGateContext = createContext(null);

export const useSubscriptionGate = () => useContext(SubscriptionGateContext);

const STATUS_COPY = {
  past_due: {
    title: 'Votre essai gratuit est terminé',
    intro: 'Pour continuer à utiliser 360 Resto, active votre abonnement en 30 secondes.',
    accent: 'from-amber-500 to-orange-600',
  },
  canceled: {
    title: 'Votre abonnement est annulé',
    intro: "Vous pouvez le réactiver à tout moment depuis l'espace de facturation.",
    accent: 'from-rose-500 to-[var(--p-f0592b)]',
  },
  incomplete: {
    title: 'Paiement en attente',
    intro: 'Stripe attend la validation de votre paiement. Terminez la procédure pour reprendre l’accès.',
    accent: 'from-[var(--brand)] to-[var(--p-f0592b)]',
  },
  unpaid: {
    title: 'Facture impayée',
    intro: 'Régularisez votre facture pour réactiver immédiatement votre espace.',
    accent: 'from-rose-500 to-[var(--p-f0592b)]',
  },
  default: {
    title: 'Abonnement requis',
    intro: 'Mettez à jour votre paiement pour reprendre l’accès à votre espace.',
    accent: 'from-rose-500 to-[var(--p-f0592b)]',
  },
};

export const SubscriptionGateProvider = ({ children }) => {
  const [gate, setGate] = useState(null); // { status, message, tenant_slug, trial_end }

  useEffect(() => {
    // Use a stable ref to the setter so the interceptor stays current across
    // React 18 strict-mode double-mounts.
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        // DISABLED (June 2026, owner request): subscription paywall removed.
        // Never surface the blocking gate modal, even if a 402 slips through.
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const dismiss = () => setGate(null);
  const value = useMemo(() => ({ gate, openGate: setGate, dismiss }), [gate]);

  return (
    <SubscriptionGateContext.Provider value={value}>
      {children}
      <SubscriptionGateModal />
    </SubscriptionGateContext.Provider>
  );
};

const SubscriptionGateModal = () => {
  const { gate, dismiss } = useSubscriptionGate();
  const { logout } = useAuth();
  const [opening, setOpening] = useState(false);

  if (!gate) return null;
  const copy = STATUS_COPY[gate.status] || STATUS_COPY.default;

  const openPortal = async () => {
    setOpening(true);
    try {
      // Try the Stripe billing portal first; fall back to the support email.
      const r = await axios.post(
        `${API}/subscription/create-portal-session`,
        { return_url: window.location.origin + window.location.pathname },
        { withCredentials: true }
      );
      const url = r?.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
    } catch {
      // Ignore — we fall back to support
    }
    setOpening(false);
    window.location.href = 'mailto:contact@360-resto.com?subject=Réactiver%20mon%20abonnement';
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-neutral-950/85 backdrop-blur-md flex items-center justify-center p-4"
        data-testid="subscription-gate"
      >
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          className="w-full max-w-md bg-[var(--carte)] rounded-3xl overflow-hidden shadow-2xl"
        >
          <div className={`bg-gradient-to-br ${copy.accent} p-6 text-[var(--sur-couleur)] relative`}>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/20 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-3">
                <Lock size={26} />
              </div>
              <h1 className="text-2xl font-extrabold leading-tight" data-testid="gate-title">
                {copy.title}
              </h1>
              <p className="text-sm text-white/95 mt-2 leading-relaxed">{copy.intro}</p>
            </div>
          </div>

          <div className="p-5 space-y-3">
            <p className="text-sm text-[var(--texte-2)]" data-testid="gate-message">
              {gate.message}
            </p>

            <button
              onClick={openPortal}
              disabled={opening}
              className="w-full bg-gradient-to-r from-[var(--brand)] to-[var(--p-f0592b)] text-[var(--sur-couleur)] rounded-2xl p-3.5 font-bold text-[15px] flex items-center justify-center gap-2 shadow-lg disabled:opacity-60"
              data-testid="gate-open-portal"
            >
              <CreditCard size={18} />
              {opening ? 'Ouverture…' : 'Activer mon abonnement'}
              <ExternalLink size={14} className="opacity-80" />
            </button>

            {gate.status !== 'past_due' && gate.status !== 'canceled' && false && (
              <button
                onClick={async () => {
                  try {
                    const API = process.env.REACT_APP_BACKEND_URL;
                    await axios.post(`${API}/api/subscription/start-trial`, {}, { withCredentials: true });
                    toast.success('Essai gratuit activé — 14 jours, sans carte.');
                    dismiss();
                    setTimeout(() => window.location.reload(), 400);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Impossible d'activer l'essai");
                  }
                }}
                className="w-full bg-[var(--carte)] border-2 rounded-2xl p-3.5 font-bold text-[15px] flex items-center justify-center gap-2"
                style={{ borderColor: 'var(--brand)', color: 'var(--brand-pressed)' }}
                data-testid="gate-start-trial"
              >
                <Sparkles size={18} /> Commencer l'essai gratuit · 14 jours · toutes vos données sont conservées
              </button>
            )}

            <a
              href="mailto:contact@360-resto.com?subject=Aide%20avec%20mon%20abonnement"
              className="w-full bg-[var(--remplissage)] hover:bg-[var(--remplissage)] text-[var(--texte)] rounded-2xl p-3 font-semibold text-[13px] flex items-center justify-center gap-2"
              data-testid="gate-contact-support"
            >
              <MailQuestion size={16} /> Contacter le support
            </a>

            <button
              onClick={async () => {
                dismiss();
                await logout();
              }}
              className="w-full text-[var(--texte-2)] hover:text-[var(--texte)] rounded-2xl p-3 font-semibold text-[12px] flex items-center justify-center gap-2"
              data-testid="gate-logout"
            >
              <LogOut size={14} /> Me déconnecter
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SubscriptionGateProvider;
